<template lang="pug">
.course-id-select.relative
  //- Label
  label.block.mb-2(v-if="label" :for="inputId")
    | {{ label }}
    span.text-red-500.required-asterisk(v-if="required") *
    span.text-sm.text-gray-500.dark_text-gray-400.ml-2(v-if="instructionalText") - {{ instructionalText }}
  
  //- Selected courses as chips
  .selected-chips.mb-2(v-if="selectedCourses.length > 0")
    .flex.flex-wrap.gap-2
      .flex.items-center.gap-1.px-3.py-1.bg-blue-700.text-white.rounded-full.text-sm.transition-all.duration-200.inline-flex(
        v-for="course in selectedCourses" 
        :key="course.id"
      )
        span {{ course.courseId }}
        button.remove-chip.w-4.h-4.flex.items-center.justify-center.rounded-full.hover_bg-blue-600.transition-all.duration-200(
          @click="removeCourse(course)"
          type="button"
          aria-label="Remove selection"
        )
          Icon(icon="mdi:close" class="w-3 h-3")
  
  //- Dropdown trigger
  .dropdown-trigger.relative
    input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.text-gray-900.dark_text-white(
      :id="inputId"
      :value="searchQuery"
      @input="updateSearch"
      @focus="openDropdown"
      @keydown="handleKeydown"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      ref="inputRef"
    )
    button.dropdown-arrow.absolute.right-2.transform.p-1.transition-transform.duration-200(
      @click="toggleDropdown"
      type="button"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      style="top: 50%; transform: translateY(-50%);"
    )
      Icon(
        :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" 
        class="w-4 h-4 text-gray-500"
      )
  
  //- Dropdown menu
  .dropdown-menu.absolute.top-full.left-0.right-0.mt-1.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.shadow-lg.z-50.max-h-60.overflow-y-auto(
    v-if="isOpen"
    ref="dropdownRef"
  )
    .p-2
      //- Search results or all options
      .option-list.space-y-1
        .option.flex.items-center.gap-2.p-2.rounded.cursor-pointer.transition-colors.duration-150.text-gray-900.dark_text-white.hover_bg-gray-100.hover_text-gray-900.dark_hover_bg-gray-600.dark_hover_text-white(
          v-for="course in filteredCourses" 
          :key="course.id"
          @click="selectCourse(course)"
          @mouseenter="highlightedIndex = filteredCourses.indexOf(course)"
          :class="{ 'bg-gray-100 text-gray-900 dark_bg-gray-600 dark_text-white': highlightedIndex === filteredCourses.indexOf(course) }"
        )
          input(
            :type="multiple ? 'checkbox' : 'radio'"
            class="w-4 h-4 text-primary-600 bg-gray-600 border-gray-500 focus:ring-primary-500"
            :checked="isCourseSelected(course)"
            @click.stop
            @change="selectCourse(course)"
          )
          span.text-sm {{ course.courseId }} - {{ course.title }}
      
      //- No results message
      .no-results.p-2.text-sm.text-gray-500.text-center(
        v-if="filteredCourses.length === 0"
      )
        | No courses found
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';

// Props
interface Course {
  id: string;
  courseId: number;
  title: string;
}

interface Props {
  modelValue: number[] | number | null;
  courses: Course[];
  placeholder?: string;
  label?: string;
  instructionalText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  valueType?: 'single' | 'array';
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select a course',
  label: '',
  instructionalText: '',
  required: false,
  disabled: false,
  multiple: false,
  valueType: 'array'
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: number[] | number | null];
}>();

// Reactive state
const isOpen = ref(false);
const searchQuery = ref('');
const highlightedIndex = ref(-1);
const inputRef = ref<HTMLInputElement>();
const dropdownRef = ref<HTMLElement>();

// Generate unique ID for accessibility
const inputId = `course-id-select-${Math.random().toString(36).substr(2, 9)}`;

// Computed properties
const selectedCourses = computed(() => {
  if (!props.modelValue) return [];

  // Handle single number value
  if (props.valueType === 'single' && typeof props.modelValue === 'number') {
    const course = props.courses.find(c => c.courseId === props.modelValue);
    return course ? [course] : [];
  }

  // Handle array value (existing behavior)
  if (Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    return props.courses.filter(course =>
      (props.modelValue as number[]).includes(course.courseId)
    );
  }

  return [];
});

const filteredCourses = computed(() => {
  if (!props.courses || props.courses.length === 0) {
    return [];
  }

  if (!searchQuery.value) {
    return props.courses;
  }

  const query = searchQuery.value.toLowerCase();
  const filtered = props.courses.filter(
    course =>
      course.title.toLowerCase().includes(query) ||
      course.courseId.toString().includes(query)
  );
  return filtered;
});

// Methods
const openDropdown = () => {
  if (!props.disabled) {
    isOpen.value = true;
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  highlightedIndex.value = -1;
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const updateSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
  highlightedIndex.value = -1;
};

const isCourseSelected = (course: Course) => {
  if (props.valueType === 'single') {
    return props.modelValue === course.courseId;
  }

  // Array mode (existing behavior)
  return (
    Array.isArray(props.modelValue) &&
    props.modelValue.includes(course.courseId)
  );
};

const selectCourse = (course: Course) => {
  if (props.valueType === 'single') {
    // Single selection mode - emit the course ID directly
    emit('update:modelValue', course.courseId);
    closeDropdown();
    searchQuery.value = '';
  } else if (props.multiple) {
    // Multiple selection mode (array)
    const currentValues = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];
    const courseIndex = currentValues.indexOf(course.courseId);

    if (courseIndex > -1) {
      // Remove if already selected
      currentValues.splice(courseIndex, 1);
    } else {
      // Add if not selected
      currentValues.push(course.courseId);
    }

    emit('update:modelValue', currentValues);
  } else {
    // Single selection mode (array with one item)
    emit('update:modelValue', [course.courseId]);
    closeDropdown();
    searchQuery.value = '';
  }
};

const removeCourse = (course: Course) => {
  if (props.valueType === 'single') {
    // Single selection mode - clear the selection
    emit('update:modelValue', null);
  } else {
    // Array mode (existing behavior)
    const currentValues = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];
    const courseIndex = currentValues.indexOf(course.courseId);

    if (courseIndex > -1) {
      currentValues.splice(courseIndex, 1);
      emit('update:modelValue', currentValues);
    }
  }
};

const clearSelection = () => {
  if (props.valueType === 'single') {
    emit('update:modelValue', null);
  } else {
    emit('update:modelValue', []);
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        filteredCourses.value.length - 1
      );
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
      break;
    case 'Enter':
      event.preventDefault();
      if (
        highlightedIndex.value >= 0 &&
        highlightedIndex.value < filteredCourses.value.length
      ) {
        selectCourse(filteredCourses.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      closeDropdown();
      break;
  }
};

// Click outside to close
const handleClickOutside = (event: Event) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node) &&
    !inputRef.value?.contains(event.target as Node)
  ) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
